/* =============================================================================
 * PIXEL DE META · carga diferida, una sola vez, y solo con permiso.
 * -----------------------------------------------------------------------------
 * POR QUE NO HAY UN `<script>` EN LINEA EN `index.html`, que es como lo pone
 * todo el mundo: porque ese es exactamente el fallo que dejo el pixel de la
 * TIENDA sin medir nada durante dias. pretix sirve
 * `script-src 'self'` sin nonce, asi que el navegador se negaba a ejecutar el
 * snippet — el codigo llegaba entero al HTML y no corria una sola instruccion.
 * Aqui el script se inyecta por DOM desde React: no depende de que ninguna
 * cabecera permita codigo en linea, y si manana esta web adopta una CSP estricta
 * seguira funcionando anadiendo un host, no un `'unsafe-inline'`.
 *
 * EL CONTRATO DE ESTE MODULO, en una linea: **nada de aqui toca la red hasta que
 * alguien llama a `enablePixel()`**, y eso solo pasa tras un si explicito.
 *
 * Y UNA COSA QUE NO ES OBVIA EN UNA SPA: `fbevents.js` dispara `PageView` una
 * vez, al cargarse. Aqui las seis paginas son la MISMA carga de documento, asi
 * que sin `trackPageView()` en cada cambio de ruta Meta veria una sola visita
 * por sesion y las cinco ciudades serian invisibles.
 * ========================================================================== */
import { META_PIXEL_ID } from './config';

const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const SCRIPT_ID = 'az-meta-pixel';

type FbqArgs = readonly unknown[];
interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: FbqArgs[];
  push: unknown;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Ya se ha inicializado. Evita un segundo `init`, que duplicaria cada evento. */
let started = false;

/**
 * El arranque canonico de Meta, escrito como funcion en vez de como cadena.
 * Crea la cola `fbq` para que los eventos que se pidan antes de que el fichero
 * termine de bajar no se pierdan: se encolan y se envian al llegar.
 */
function installQueue(): Fbq {
  if (window.fbq) return window.fbq;

  const fbq = function (this: unknown, ...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;

  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';

  window.fbq = fbq;
  window._fbq ??= fbq;
  return fbq;
}

/** Mete `fbevents.js` en la pagina. Idempotente: dos llamadas, un solo script. */
function injectScript(): void {
  if (document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
}

/**
 * Enciende la medicion. **Solo debe llamarse con consentimiento concedido.**
 *
 * Manda el `PageView` de la pagina en la que el visitante dijo que si, que si no
 * se perderia: `fbevents.js` solo lo dispara solo cuando el `init` ocurre antes
 * de que el fichero cargue, y aqui el orden es justo el contrario.
 *
 * DEVUELVE si ha arrancado EN ESTA LLAMADA, y ese booleano no es un detalle: es
 * lo que impide contar dos veces la primera pagina. Quien llama sabe asi que el
 * `PageView` de esta vista ya ha salido y no debe mandar otro. Sin esto, la
 * primera ruta de cada sesion valdria doble y todos los ratios de conversion
 * saldrian a la mitad de lo que son.
 */
export function enablePixel(): boolean {
  if (started || typeof window === 'undefined') return false;
  started = true;

  const fbq = installQueue();
  injectScript();
  fbq('init', META_PIXEL_ID);
  fbq('track', 'PageView');
  return true;
}

/** Una visita mas. En una SPA hay que decirlo a mano en cada cambio de ruta. */
export function trackPageView(): void {
  if (!started || !window.fbq) return;
  window.fbq('track', 'PageView');
}

/**
 * Ficha de destino vista. Es el evento con el que Meta arma publicos de
 * remarketing por ciudad ("quien miro Bogota"), que es lo que hace que la
 * inversion en anuncios se pueda segmentar antes de que haya una sola venta.
 */
export function trackViewContent(content: { id: string; name: string }): void {
  if (!started || !window.fbq) return;
  window.fbq('track', 'ViewContent', {
    content_type: 'product',
    content_ids: [content.id],
    content_name: content.name,
  });
}

/** El visitante pulsa el CTA de entradas: intencion de compra, antes de salir. */
export function trackInitiateCheckout(content: { id: string; name: string }): void {
  if (!started || !window.fbq) return;
  window.fbq('track', 'InitiateCheckout', {
    content_type: 'product',
    content_ids: [content.id],
    content_name: content.name,
  });
}

/**
 * El visitante pulsa el CTA de un destino QUE NO VENDE ENTRADA y se va a
 * WhatsApp (Funes, 23-sep-2026). Es `Contact`, el evento estandar de Meta para
 * justo esto, y NO `InitiateCheckout`.
 *
 * POR QUE IMPORTA Y NO ES UNA FINURA. `InitiateCheckout` es la senal con la que
 * Meta aprende a quien ensenar los anuncios, y su pareja natural es el
 * `Purchase` que cierra la tienda. Funes no tiene ficha de tienda: jamas habra
 * un `Purchase` detras. Meter sus clics en el mismo cubo que los de las cinco
 * ciudades que si venden ensucia el embudo entero y ensena al optimizador a
 * buscar gente que nunca podra comprar.
 */
export function trackContact(content: { id: string; name: string }): void {
  if (!started || !window.fbq) return;
  window.fbq('track', 'Contact', {
    content_type: 'product',
    content_ids: [content.id],
    content_name: content.name,
  });
}

/** Solo para las pruebas: olvida que ya se arranco. */
export function resetPixelForTests(): void {
  started = false;
  delete window.fbq;
  delete window._fbq;
  document.getElementById(SCRIPT_ID)?.remove();
}
